import z from 'zod';
import { FormFunctions } from './DynamicFormDisplay';

export const CustomBehaviorOptionSchema = z.union([z.string(), z.unknown()]);
type CustomBehaviorOption = z.infer<typeof CustomBehaviorOptionSchema>;

// CustomBehaviorFunction is a function that takes a field value and returns a value before that value is updated in
// the form.  Also, this function can be used to do things at the form level (e.g. disable all validations when a field
// is set to some value).
type CustomBehaviorFunction = (
  // value is the value of the field that the custom behavior is being applied to.
  value: unknown,

  // formFunctions is an object that contains functions that can be used to manipulate the form at the top level (e.g.
  // disable all validations and in the future other things?).
  formFunctions: FormFunctions,

  // options is an object that contains any options that are passed to the custom behavior.  This can be used to pass
  // any additional information needed to run the custom behavior.
  options?: Record<string, CustomBehaviorOption>
) => unknown;

export const customBehaviors: Record<string, CustomBehaviorFunction> = {
  // disable_all_requires_when_set is a custom behavior that disables all require validation on a form when the field
  // is set to any value (e.g. a checkbox being checked or a text field being filled out would disable all "required"
  // validators on the form).
  disable_all_requires_when_set: (value, formFunctions) => {
    let isValidationEnabled = true;
    if (typeof value === 'boolean' || value === undefined) {
      isValidationEnabled = !value;
    } else if (typeof value === 'string') {
      isValidationEnabled = value.length === 0;
    } else if (typeof value === 'object') {
      isValidationEnabled = Object.keys(value || {}).length === 0;
    } else if (typeof value === 'number') {
      // Because js evaluates a 0 as false and anything else as true; we assume that if the value is a number, then the
      // value has been set, and we disable validation (undefined is handled above).
      isValidationEnabled = false;
    }

    formFunctions.setIsValidationEnabled(isValidationEnabled);
    return value;
  }
} as const;
